/**
 * When to ask for notification permission. The rule: at a moment that has
 * already proved the point, and never twice in one sitting.
 *
 * The permission prompt is not like the install prompt, and the difference
 * decides this whole module. The install step can be shown every session
 * forever, because dismissing it costs nothing — the offer simply returns.
 * `Notification.requestPermission()` is the opposite: it can be answered
 * **no** exactly once, and that no is permanent. The browser will not show the
 * dialog again, and nothing in the app can undo it. A user who taps "block"
 * has silently opted out of the product's only job, and the only route back is
 * buried in browser site settings.
 *
 * So the native dialog is never fired cold. It is fired only after the user has
 * said yes to OUR dialog, which costs nothing to decline and can be asked again
 * later. Everything here is about deciding when that soft ask is due.
 *
 * The trigger is contextual rather than timed — see
 * docs/decisions/0020-web-push-over-vapid.md. This module does not know
 * which trigger fired; it only decides whether an ask is due at all.
 *
 * Pure, so the rule is testable rather than something you observe over a week.
 */

/**
 * `unsupported` is a real and common state, not an error: an iOS browser tab
 * has no `PushManager` at all until the app is on the home screen, and every
 * desktop Firefox with notifications turned off at the browser level lands
 * here too. It must never produce an ask, because there is nothing to ask for.
 */
export type PushPermission = "default" | "granted" | "denied" | "unsupported";

export interface PushMemory {
  /** How many times, across all sessions, they have said "not now". */
  dismissals: number;
  /** The last time the soft ask was shown, epoch ms. */
  lastAskedMs: number | null;
}

export const NO_PUSH_MEMORY: PushMemory = { dismissals: 0, lastAskedMs: null };

/**
 * How long to wait after each refusal, in days.
 *
 * The first ask is immediate — it fires on the first trigger, which is the
 * moment the user has just created something with a date on it and the promise
 * is concrete. After that it backs off hard. Someone who has said no four times
 * is asked once a month, which is close enough to never that it stays out of
 * the way, without permanently closing a door they may want later when their
 * first item actually goes off.
 */
export const BACKOFF_DAYS = [0, 1, 3, 7, 30];

const DAY_MS = 86_400_000;

/** The wait owed after `dismissals` refusals. Saturates at the last entry. */
export function waitMs(dismissals: number): number {
  const i = Math.min(Math.max(dismissals, 0), BACKOFF_DAYS.length - 1);
  return (BACKOFF_DAYS[i] ?? 0) * DAY_MS;
}

export interface AskInput {
  permission: PushPermission;
  /** A live subscription is already saved for this device. */
  subscribed: boolean;
  memory: PushMemory;
  /** This app-open has already shown the soft ask. */
  askedThisSession: boolean;
  nowMs: number;
}

/**
 * Should the soft ask appear at the trigger that just fired?
 *
 * Note what is NOT here: any notion of how many items the user has, or how long
 * they have used the app. The trigger itself is the qualification — it only
 * fires after an item with a date exists, which is the only situation in which
 * a reminder means anything.
 */
export function askDue({
  permission,
  subscribed,
  memory,
  askedThisSession,
  nowMs,
}: AskInput): boolean {
  // Nothing to ask for, and nothing an ask could change.
  if (permission === "unsupported") return false;

  /**
   * Already blocked at the browser level. Showing a friendly sheet here would
   * be a lie: tapping "yes, remind me" could not produce a notification, and
   * the native dialog will never appear again to explain why. Settings carries
   * the recovery instructions instead, where someone looking for them will be.
   */
  if (permission === "denied") return false;

  /**
   * Granted but not subscribed is a real state — a cleared site storage, a new
   * device, an expired subscription — and it is emphatically NOT an ask. The
   * user already gave permission; re-subscribing needs no dialog and happens
   * silently. Asking again would be asking for something we already have.
   */
  if (permission === "granted") return false;

  if (subscribed) return false;
  if (askedThisSession) return false;

  const last = memory.lastAskedMs;
  if (last === null) return true;
  return nowMs - last >= waitMs(memory.dismissals);
}

/** They said "not now" to our sheet. The native dialog was never fired. */
export function declined(m: PushMemory, nowMs: number): PushMemory {
  return { dismissals: m.dismissals + 1, lastAskedMs: nowMs };
}

/**
 * They said yes to our sheet, so the native dialog was fired.
 *
 * Recorded whatever the browser then returned. A "yes" to us followed by a
 * "block" in the native dialog must still advance the clock — otherwise the
 * next trigger re-opens the sheet for a permission that can no longer be
 * granted. `askDue` catches that case anyway via `denied`, but the memory
 * should not claim we never asked.
 */
export function accepted(m: PushMemory, nowMs: number): PushMemory {
  return { ...m, lastAskedMs: nowMs };
}

// ── Persistence ───────────────────────────────────────────────────────
//
// localStorage, for the same reason the install nudge uses it: permission is
// granted per browser per device, not per account. Syncing it would mean a user
// who allowed notifications on their phone is never asked on their tablet,
// where the permission does not exist.

const KEY = "larder.push.v1";
/** sessionStorage: one soft ask per app-open, surviving a reload. */
const SESSION_KEY = "larder.push.asked";

export function readPushMemory(store: Storage | undefined = safeLocal()): PushMemory {
  if (!store) return NO_PUSH_MEMORY;
  try {
    const raw = store.getItem(KEY);
    if (!raw) return NO_PUSH_MEMORY;
    const parsed = JSON.parse(raw) as Partial<PushMemory>;
    return {
      dismissals: typeof parsed.dismissals === "number" ? parsed.dismissals : 0,
      lastAskedMs: typeof parsed.lastAskedMs === "number" ? parsed.lastAskedMs : null,
    };
  } catch {
    return NO_PUSH_MEMORY;
  }
}

export function writePushMemory(m: PushMemory, store: Storage | undefined = safeLocal()) {
  try {
    store?.setItem(KEY, JSON.stringify(m));
  } catch {
    // Private mode and storage-full both throw. Losing the record costs one
    // extra ask next session; throwing would take down the screen the user is
    // actually trying to use.
  }
}

/**
 * The endpoint we last wrote to Firestore for this account on this device.
 *
 * Exists purely to keep the launch-time heal FREE in the overwhelmingly common
 * case, where nothing has changed. Without it every app-open would re-save an
 * identical document — a Firestore write per user per launch, bought for
 * nothing, in a product that meters its spend carefully everywhere else.
 *
 * Losing this record is harmless: it costs one redundant write, which is
 * exactly the behaviour we would have had anyway.
 */
const SAVED_KEY = "larder.push.saved";

export function lastSavedEndpoint(uid: string, store = safeLocal()): string | null {
  try {
    return store?.getItem(`${SAVED_KEY}.${uid}`) ?? null;
  } catch {
    return null;
  }
}

export function rememberSavedEndpoint(
  uid: string,
  endpoint: string | null,
  store = safeLocal(),
) {
  try {
    if (endpoint === null) store?.removeItem(`${SAVED_KEY}.${uid}`);
    else store?.setItem(`${SAVED_KEY}.${uid}`, endpoint);
  } catch {
    // See writePushMemory.
  }
}

export function pushAskedThisSession(uid: string, store = safeSession()): boolean {
  try {
    return store?.getItem(`${SESSION_KEY}.${uid}`) === "1";
  } catch {
    return false;
  }
}

export function markPushAskedThisSession(uid: string, store = safeSession()) {
  try {
    store?.setItem(`${SESSION_KEY}.${uid}`, "1");
  } catch {
    // See writePushMemory.
  }
}

function safeLocal(): Storage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

function safeSession(): Storage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.sessionStorage;
  } catch {
    return undefined;
  }
}
